/**
 * Email, on the post-send 7 day match.
 *
 * Matched revenue is computed from paid orders, never taken from the provider, and it is
 * labelled EMAIL MATCHED rather than being treated as a fourth attribution basis. One order
 * counts once, against the most recent qualifying send.
 *
 * Open rate is shown with its caveat attached and is never the headline. Apple Mail Privacy
 * Protection pre-fetches images, so an open is evidence that a mail server touched the
 * message, not that a person read it.
 */
import { useState } from 'react';
import type { WindowKind } from '@elutide/shared';
import { Card, DataTable, EmptyState, ErrorState, MoneyText, Pill, Skeleton, type Column } from '@/components';
import { formatMoney, formatPercent } from '@/lib/format';
import { WindowChips } from '@/features/finance/Overview';
import { useEmailCampaigns } from './api';
import { FreshnessStrip } from './Acquisition';
import type { EmailRow } from './types';
import '@/features/finance/finance.css';

export function EmailPage() {
  const [window, setWindow] = useState<WindowKind>('90d');
  const query = useEmailCampaigns(window);

  if (query.isLoading) return <Skeleton shape="card" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        message="Email campaigns could not be loaded."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const data = query.data;
  const totalMatched = data.items.reduce((sum, row) => sum + row.matched_revenue_cents, 0);
  const totalDelivered = data.items.reduce((sum, row) => sum + row.delivered, 0);

  const columns: readonly Column<EmailRow>[] = [
    { id: 'campaign', header: 'Campaign', priority: 1, render: (r) => r.campaign_name ?? r.campaign_id },
    { id: 'sent_at', header: 'Sent', priority: 1, mono: true, render: (r) => r.sent_at.slice(0, 10) },
    { id: 'delivered', header: 'Delivered', priority: 2, align: 'right', render: (r) => r.delivered },
    {
      id: 'open_rate',
      header: 'Open rate',
      priority: 4,
      align: 'right',
      render: (r) => (
        <span title={data.open_rate_caveat} className="o-muted">
          {formatPercent(r.open_rate)}
        </span>
      ),
    },
    { id: 'click_rate', header: 'Click rate', priority: 3, align: 'right', render: (r) => formatPercent(r.click_rate) },
    { id: 'orders', header: 'Matched orders', priority: 2, align: 'right', render: (r) => r.matched_orders },
    {
      id: 'matched',
      header: 'Matched revenue',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.matched_revenue_cents} showCents={false} />,
    },
    {
      id: 'per_1k',
      header: 'Per 1k delivered',
      priority: 3,
      align: 'right',
      money: true,
      render: (r) =>
        r.revenue_per_1k_delivered_cents === null ? (
          <span className="o-muted">n/a</span>
        ) : (
          <MoneyText cents={r.revenue_per_1k_delivered_cents} showCents={false} />
        ),
    },
  ];

  return (
    <div className="o-stack">
      <WindowChips value={window} onChange={setWindow} />

      <Card
        title="Email campaigns"
        basis="woo_paid"
        action={
          <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <Pill tone="info">EMAIL MATCHED · 7d</Pill>
            <FreshnessStrip sources={data.freshness} />
          </span>
        }
      >
        <div className="o-scorecard">
          <div className="o-kpi">
            <span className="o-kpi__label">Matched revenue</span>
            <span className="o-kpi__value">{formatMoney(totalMatched, { showCents: false })}</span>
            <span className="o-kpi__meta">{data.items.length} sends in this window</span>
          </div>
          <div className="o-kpi">
            <span className="o-kpi__label">Delivered</span>
            <span className="o-kpi__value">{totalDelivered.toLocaleString('en-US')}</span>
            <span className="o-kpi__meta">
              {totalDelivered === 0
                ? 'no sends'
                : `${formatMoney(Math.round((totalMatched / totalDelivered) * 1000), { showCents: false })} per 1k`}
            </span>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={data.items}
          rowKey={(r) => r.campaign_id}
          caption="Email sends and the revenue matched to them"
          emptyState={<EmptyState message="No sends in this window." />}
          allowHorizontalScroll
        />

        <p className="o-muted">{data.match_rule}</p>
        <p className="o-muted">{data.open_rate_caveat}</p>
      </Card>
    </div>
  );
}
